import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { formatDateTime, formatMoney } from './billingShared';
import { studentLabel } from '../utils/studentLabel';

function uniqueAccountTotals(rows) {
  const seen = new Set();
  let prepaid = 0;
  let debt = 0;
  for (const row of rows) {
    const key = row.account_id ?? `student-${row.student_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    prepaid += Number(row.prepaid) || 0;
    debt += Number(row.debt) || 0;
  }
  return { prepaid, debt, balance: prepaid - debt };
}

function mergeBillingRows(rows, layoutItems) {
  const byId = new Map(rows.map((row) => [row.student_id, row]));
  const merged = [];
  const seen = new Set();
  for (const item of layoutItems) {
    if (item.type === 'divider') {
      merged.push({
        kind: 'divider',
        key: item.id != null ? `d-${item.id}` : `d-tmp-${merged.length}`,
        title: item.title || '',
      });
    } else if (item.type === 'student' && byId.has(item.id)) {
      merged.push({ kind: 'student', key: `s-${item.id}`, row: byId.get(item.id) });
      seen.add(item.id);
    }
  }
  for (const row of rows) {
    if (!seen.has(row.student_id)) {
      merged.push({ kind: 'student', key: `s-${row.student_id}`, row });
    }
  }
  return merged;
}

export default function TeacherBillingPage() {
  const [rows, setRows] = useState([]);
  const [layoutItems, setLayoutItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [billingRes, layoutRes] = await Promise.all([
          api.get('/payments/teacher/students/'),
          api.get('/teacher/students/layout/').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setRows(Array.isArray(billingRes.data) ? billingRes.data : []);
        setLayoutItems(Array.isArray(layoutRes.data) ? layoutRes.data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Не удалось загрузить оплату учеников.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => uniqueAccountTotals(rows), [rows]);
  const displayRows = useMemo(() => mergeBillingRows(rows, layoutItems), [rows, layoutItems]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Оплата учеников</h1>
        <p className="page-subtitle">
          Предоплата и долг по семейным счетам. Нажмите на ученика, чтобы увидеть историю и оформить занятие.
        </p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {rows.length === 0 ? (
        <p className="text-secondary">Нет активных учеников.</p>
      ) : (
        <>
          <div className="billing-totals">
            <div className="billing-totals-main">
              <span className="billing-totals-label">Общий баланс</span>
              <strong
                className={
                  totals.balance < 0
                    ? 'text-error'
                    : totals.balance > 0
                      ? 'text-success'
                      : undefined
                }
              >
                {formatMoney(totals.balance)}
              </strong>
            </div>
            <p className="billing-totals-note">
              предоплата {formatMoney(totals.prepaid)} − долг {formatMoney(totals.debt)}
            </p>
          </div>
          <table className="table billing-students-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Ставка</th>
                <th>Предоплата / долг</th>
                <th>Последний платёж</th>
                <th>Всего внесено</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((item) => {
                if (item.kind === 'divider') {
                  return (
                    <tr key={item.key} className="teacher-students-divider-row">
                      <td colSpan={5}>
                        <div className="teacher-students-divider">
                          <span className="teacher-students-divider-line" aria-hidden />
                          {item.title ? (
                            <span className="teacher-students-divider-title">{item.title}</span>
                          ) : null}
                          <span className="teacher-students-divider-line" aria-hidden />
                        </div>
                      </td>
                    </tr>
                  );
                }
                const row = item.row;
                return (
                  <tr key={item.key}>
                    <td>
                      <Link to={`/teacher/billing/${row.student_id}`}>
                        {studentLabel(row)}
                        {row.username ? (
                          <span className="text-secondary"> @{row.username}</span>
                        ) : null}
                      </Link>
                    </td>
                    <td>{row.rate_per_hour ? formatMoney(row.rate_per_hour) : '—'}</td>
                    <td className={row.status === 'debt' ? 'text-error' : 'text-success'}>
                      {row.status === 'debt'
                        ? `Долг ${formatMoney(row.debt)}`
                        : `Предоплата ${formatMoney(row.prepaid)}`}
                    </td>
                    <td>
                      {formatDateTime(row.last_payment_at)}
                      {row.last_payment_amount ? ` · ${formatMoney(row.last_payment_amount)}` : ''}
                    </td>
                    <td>{formatMoney(row.total_deposited)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
